import React from 'react'
import { groupBy } from 'lodash'

import Dropdown from 'components/Dropdown'
import Tooltip from 'components/Tooltip'
import InfiniteList, { handleFetchMore } from 'components/InfiniteList'

import {
  ArtboardDetailInfoFragment,
  useGetArtboardsForDocumentQuery,
} from 'gqlTypes'
import { useVersioning } from 'modules/versioning'

import {
  DropdownContentContainer,
  DropdownTarget,
  DropdownIcon,
} from './ArtboardsDropdown.styles'

interface ArtboardsDropdownContainerProps {
  shareIdentifier: string
  artboard: ArtboardDetailInfoFragment
  /* This prop "collapse" is send via "React.cloneElement" from the Breadcrumbs component */
  collapse?: boolean
}

const entriesPath = ['share', 'version', 'document', 'artboards', 'entries']

const formatArtboardPages = (
  artboards: ArtboardDetailInfoFragment[],
  buildRouteLink: (permanentArtboardShortId: string) => string
) => {
  const dropdownItems: React.ReactNode[] = []

  const pages = Object.entries(
    groupBy(artboards, artboard => artboard.page?.identifier)
  )

  pages.forEach(([pageName, artboards], pageIndex) => {
    if (pageName) {
      // Add a divider to separate this page from the previous
      pageIndex > 0 &&
        dropdownItems.push(<Dropdown.Divider key={`${pageIndex}-divider`} />)

      dropdownItems.push(
        <Dropdown.Header key={pageIndex}>
          {artboards[0].page?.name}
        </Dropdown.Header>
      )
    }

    artboards.forEach(artboard => {
      const { identifier, permanentArtboardShortId, name } = artboard
      const link = buildRouteLink(permanentArtboardShortId!)

      dropdownItems.push(
        <Dropdown.Item.NavLink key={identifier} to={link}>
          {name}
        </Dropdown.Item.NavLink>
      )
    })
  })

  return dropdownItems
}

export const ArtboardsDropdownContainer: React.FC<ArtboardsDropdownContainerProps> = props => {
  const { artboard, shareIdentifier, collapse } = props
  const { versionShortId, getPathname } = useVersioning()

  const { data, fetchMore } = useGetArtboardsForDocumentQuery({
    variables: { identifier: shareIdentifier, versionShortId },
  })

  const artboards = data?.share?.version?.document?.artboards?.entries || []

  const after = data?.share?.version?.document?.artboards?.meta?.after || null
  const onLoadMore = handleFetchMore(fetchMore, entriesPath, {
    after,
  })

  const dropdownItems = formatArtboardPages(
    artboards,
    permanentArtboardShortId =>
      getPathname({
        routeKey: 'ARTBOARD_DETAIL',
        routeParams: {
          shareID: shareIdentifier,
          permanentArtboardShortId,
        },
      })
  )

  return (
    <Dropdown
      data-testid="artboard-selector"
      spacing="19px"
      contentPadding="0"
      toggle={dropdownIsVisible => (
        <DropdownTarget data-testid="artboard-selector-toggle">
          {!collapse ? (
            artboard.name
          ) : (
            <Tooltip
              placement="bottom"
              disabled={dropdownIsVisible}
              content={artboard.name}>
              &hellip;
            </Tooltip>
          )}{' '}
          <DropdownIcon />
        </DropdownTarget>
      )}>
      <DropdownContentContainer data-testid="page-selector-dropdown">
        <InfiniteList onLoadMore={onLoadMore}>{dropdownItems}</InfiniteList>
      </DropdownContentContainer>
    </Dropdown>
  )
}

export default ArtboardsDropdownContainer
